import type { Course, CourseInstructorDetail, CourseReview } from '../mocks/courses';
import type { Mentor } from '../mocks/mentors';
import type {
  AuthMentorBookingResp,
  AuthOrderInfoResp,
  AuthUserCourseResp,
  CategoryResp,
  CourseCommentResp,
  CourseListResp,
  CourseResp,
  LecturerResp,
  MembershipOrderResp
} from './api';

const gradients = [
  'from-violet-600 to-indigo-700',
  'from-emerald-600 to-teal-700',
  'from-sky-600 to-blue-800',
  'from-pink-500 to-violet-600',
  'from-teal-500 to-cyan-700',
  'from-orange-500 to-rose-500'
];

const icons = [
  'ri-book-open-line',
  'ri-cpu-line',
  'ri-server-line',
  'ri-bar-chart-box-line',
  'ri-product-hunt-line',
  'ri-trophy-line'
];

const mentorColors = ['#76B900', '#4285F4', '#00A4EF', '#FF9900', '#0082FB', '#9333EA'];

function pickByIndex<T>(source: T[], id: number | string | undefined) {
  const value = typeof id === 'string' ? Number(id) : id ?? 0;
  return source[Math.abs(Number(value) || 0) % source.length];
}

function formatDuration(seconds?: number) {
  if (!seconds) {
    return '待更新';
  }
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const restMinutes = minutes % 60;
    return restMinutes ? `${hours} 小時 ${restMinutes} 分鐘` : `${hours} 小時`;
  }
  return `${minutes} 分鐘`;
}

function formatCurrency(value?: number) {
  return Number(value ?? 0);
}

function formatLastUpdated(gmtModified?: string, gmtCreate?: string) {
  const raw = (gmtModified || gmtCreate || '').trim();
  if (!raw) {
    return undefined;
  }
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return new Intl.DateTimeFormat('zh-HK', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(parsed);
}

function splitKeywords(value?: string) {
  return (value ?? '')
    .split(/[、,，/|]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeLineText(value?: string) {
  if (!value) {
    return '';
  }
  return value
    .replace(/<\/(p|div|li|h\d)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
}

function splitLines(value?: string) {
  return normalizeLineText(value)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function parseCourseInfoLines(value?: string) {
  return splitLines(value).map((line, idx) => {
    const splitIdx = line.indexOf(':') >= 0 ? line.indexOf(':') : line.indexOf('：');
    if (splitIdx > 0) {
      const label = line.slice(0, splitIdx).trim();
      const content = line.slice(splitIdx + 1).trim();
      if (label && content) {
        return { label, value: content };
      }
    }
    return { label: `資訊 ${idx + 1}`, value: line };
  });
}

function idToKey(id: number | string | undefined) {
  return String(id ?? '');
}

function categoryMapFromTree(categories: CategoryResp[]) {
  const map = new Map<string, string>();
  const walk = (items: CategoryResp[]) => {
    items.forEach((item) => {
      map.set(idToKey(item.id), item.categoryName);
      if (item.list?.length) {
        walk(item.list);
      }
    });
  };
  walk(categories);
  return map;
}

export function buildCategoryOptions(categories: CategoryResp[]) {
  const flat = categories.flatMap((item) => (item.list?.length ? item.list : [item]));
  return flat.map((item) => ({
    id: item.id,
    name: item.categoryName,
    icon: item.categoryIcon || pickByIndex(icons, item.id),
    bg: item.categoryBg || ''
  }));
}

export function adaptCourseListItem(
  course: CourseListResp,
  categoryName?: string,
  categoryMeta?: { icon?: string; bg?: string }
): Course {
  const keywords = splitKeywords(course.lectureInfo?.tags || course.lectureInfo?.expertiseAreas);
  const coursePrice = formatCurrency(course.coursePrice);
  const rulingPrice = formatCurrency(course.rulingPrice || course.coursePrice || 0);
  const memberPrice = formatCurrency(course.memberPrice);

  return {
    id: String(course.id),
    categoryId: String(course.categoryId ?? ''),
    title: course.courseName || '未命名課程',
    subtitle: course.introduce || '課程介紹待補充',
    description: course.introduce || '課程介紹待補充',
    instructor: course.lectureInfo?.lecturerName || '官方講師',
    instructorCompany: course.lectureInfo?.lecturerPosition || 'NexusGo',
    level: course.courseLevel || (course.isFree ? '入門' : '精選'),
    duration: '待更新',
    students: Number(course.learnCount ?? 0),
    rating: Number((4.8 + (Number(course.id) % 20) / 100).toFixed(2)),
    reviews: Math.max(1, Number(course.learnCount ?? 0)),
    price: coursePrice,
    originalPrice: rulingPrice || coursePrice,
    memberPrice,
    tag: course.isFree ? '免費' : '精選',
    tagColor: course.isFree ? 'bg-emerald-500' : 'bg-violet-600',
    gradient: pickByIndex(gradients, course.id),
    icon: categoryMeta?.icon || pickByIndex(icons, course.id),
    category: categoryName || '未分類',
    topics: keywords.length ? keywords.slice(0, 4) : ['職涯成長', '實戰教學'],
    lastUpdated: formatLastUpdated(course.gmtModified, course.gmtCreate),
    language: course.courseLanguage || course.lectureInfo?.languages || '中文',
    certificate: !course.isFree
  };
}

export function adaptCourseDetail(
  course: CourseResp,
  categoryName?: string,
  categoryMeta?: { icon?: string; bg?: string }
): Course {
  const base = adaptCourseListItem(
    {
      ...course,
      lectureInfo: {
        lecturerName: course.lecturerResp?.lecturerName,
        lecturerPosition: course.lecturerResp?.lecturerPosition,
        lecturerHead: course.lecturerResp?.lecturerHead,
        tags: course.lecturerResp?.tags,
        languages: course.lecturerResp?.languages,
        expertiseAreas: course.lecturerResp?.expertiseAreas
      }
    },
    categoryName,
    categoryMeta
  );

  const chapters = (course.chapterRespList || []).map((chapter) => ({
    id: String(chapter.id),
    title: chapter.chapterName || '未命名章節',
    description: chapter.chapterDesc || '章節說明待補充',
    lessons: (chapter.periodRespList || []).map((period) => ({
      id: String(period.id),
      title: period.periodName || '未命名課時',
      duration: formatDuration(period.resourceResp?.videoLength),
      type: 'video' as const,
      preview: period.isFree === 1
    }))
  }));

  const objectiveList = splitLines(course.learningObjectives);
  const requirementList = splitLines(course.learningRequirements);
  const includesList = splitLines(course.courseIncludes);
  const infoList = parseCourseInfoLines(course.courseInfo);

  const instructorDetail: CourseInstructorDetail | undefined = course.lecturerResp
    ? {
        mentorId: course.lecturerResp.id ? String(course.lecturerResp.id) : undefined,
        name: course.lecturerResp.lecturerName || '官方講師',
        title: course.lecturerResp.lecturerPosition || '講師',
        company: categoryName || 'NexusGo',
        companyLogo: '',
        companyIcon: base.icon,
        avatar: course.lecturerResp.lecturerHead || '',
        bio: course.lecturerResp.introduce || '講師介紹待補充',
        experience: '實戰導師',
        stats: [
          { label: '購買學員', value: String(course.countBuy ?? 0) },
          { label: '正在學習', value: String(course.countStudy ?? 0) },
          { label: '課程章節', value: String(chapters.length) }
        ],
        expertise: splitKeywords(course.lecturerResp.expertiseAreas || course.lecturerResp.tags)
      }
    : undefined;

  return {
    ...base,
    categoryId: String(course.categoryId ?? base.categoryId ?? ''),
    duration: formatDuration(
      (course.chapterRespList || []).reduce((total, chapter) => {
        const periodTotal = (chapter.periodRespList || []).reduce(
          (sum, period) => sum + Number(period.resourceResp?.videoLength ?? 0),
          0
        );
        return total + periodTotal;
      }, 0)
    ),
    description: course.introduce || '課程介紹待補充',
    students: Number(course.countStudy ?? course.countBuy ?? 0),
    learningObjectives: objectiveList.length ? objectiveList : undefined,
    requirements: requirementList.length ? requirementList : undefined,
    discountEndTime: course.discountEndTime,
    level: course.courseLevel || base.level,
    language: course.courseLanguage || base.language,
    courseIncludes: includesList.length ? includesList : undefined,
    courseInfo: infoList.length ? infoList : undefined,
    chapters,
    instructorDetail,
    certificate: true
  };
}

export function adaptCourseComments(comments: CourseCommentResp[]): CourseReview[] {
  return comments.map((comment, index) => ({
    id: String(comment.id),
    name: comment.usersVO?.nickname || `學員 ${index + 1}`,
    avatar: comment.usersVO?.userHead || '',
    rating: 5,
    date: comment.gmtCreate || '',
    content: comment.commentText,
    company: 'NexusGo 學員',
    result: '真實評價'
  }));
}

export function adaptLecturer(lecturer: LecturerResp): Mentor {
  const skills = splitKeywords(lecturer.tags || lecturer.expertiseAreas);
  return {
    id: String(lecturer.id),
    name: lecturer.lecturerName || '未命名講師',
    title: lecturer.lecturerPosition || '講師',
    company: lecturer.lecturerPosition || 'NexusGo',
    companyColor: pickByIndex(mentorColors, lecturer.id),
    avatar: lecturer.lecturerHead || '',
    skills: skills.length ? skills.slice(0, 4) : ['職涯諮詢'],
    rating: Number((4.85 + (Number(lecturer.id) % 12) / 100).toFixed(2)),
    reviews: 20 + (Number(lecturer.id) % 50),
    price: Number(lecturer.price ?? 0),
    consultDuration: lecturer.priceUnit || '60 分鐘',
    consultHighlights: skills.length ? skills.map((item) => `${item} 深度指導`) : ['一對一深度諮詢'],
    sessions: 30 + (Number(lecturer.id) % 200),
    languages: splitKeywords(lecturer.languages).length ? splitKeywords(lecturer.languages) : ['中文'],
    bio: lecturer.introduce || '講師介紹待補充',
    expertise: splitKeywords(lecturer.expertiseAreas).length
      ? splitKeywords(lecturer.expertiseAreas)
      : ['職涯規劃'],
    timezone: lecturer.timezone || 'UTC+8',
    category: splitKeywords(lecturer.expertiseAreas)[0] || '職涯諮詢'
  };
}

export function adaptMyCourse(item: AuthUserCourseResp) {
  const course = adaptCourseDetail(
    {
      id: item.courseResp?.id || item.courseId,
      courseName: item.courseResp?.courseName,
      courseLogo: item.courseResp?.courseLogo,
      introduce: item.courseResp?.introduce,
      coursePrice: item.courseResp?.coursePrice,
      rulingPrice: item.courseResp?.rulingPrice,
      isFree: 0,
      categoryId: 0,
      lecturerId: 0,
      allowStudy: 1,
      chapterRespList: item.courseResp?.chapterRespList,
      lecturerResp: item.courseResp?.lecturerResp
    },
    undefined
  );

  const totalLessons = course.chapters?.reduce((sum, chapter) => sum + chapter.lessons.length, 0) ?? 0;
  const progress = Math.max(0, Math.min(100, Math.round(Number(item.courseProgress ?? 0))));
  const completedLessons = totalLessons ? Math.round((progress / 100) * totalLessons) : 0;

  return {
    id: course.id,
    orderId: `COURSE-${item.id}`,
    title: course.title,
    instructor: course.instructor,
    instructorCompany: course.instructorCompany,
    purchaseDate: item.gmtCreate || '',
    price: course.price,
    progress,
    totalLessons,
    completedLessons,
    duration: course.duration,
    category: course.category,
    level: course.level,
    gradient: course.gradient,
    icon: course.icon,
    thumbnail: '',
    lastWatched: item.periodName || null,
    certificate: progress === 100
  };
}

export function adaptOrder(item: AuthOrderInfoResp) {
  return {
    id: String(item.orderNo),
    orderId: String(item.orderNo),
    courseId: item.courseId ? String(item.courseId) : '',
    type: 'course' as const,
    title: item.courseName || '課程訂單',
    subtitle: item.courseLogo ? '課程訂單' : 'NexusGo',
    amount: Number(item.coursePrice ?? 0),
    date: item.payTime || item.gmtCreate || '',
    status: item.orderStatus === 2 ? 'paid' as const : item.orderStatus === 1 ? 'pending' as const : 'closed' as const
  };
}

export function adaptMembershipOrder(item: MembershipOrderResp) {
  return {
    id: String(item.id),
    orderId: String(item.orderNo),
    title: item.planName || '會員訂單',
    amount: Number(item.payPrice ?? 0),
    date: item.payTime || item.gmtCreate || '',
    expireAt: item.memberEndTime || '',
    status: item.orderStatus === 2 ? 'paid' as const : item.orderStatus === 1 ? 'pending' as const : 'closed' as const
  };
}

export function adaptMentorBooking(item: AuthMentorBookingResp) {
  return {
    id: String(item.id),
    bookingId: String(item.bookingNo),
    mentorId: String(item.lecturerId),
    mentorName: item.lecturerName || '導師',
    mentorAvatar: item.lecturerHead || '',
    mentorTitle: item.lecturerPosition || '',
    contactName: item.contactName || '',
    contactEmail: item.contactEmail || '',
    contactPhone: item.contactPhone || '',
    preferTime: item.preferTime || '',
    consultGoal: item.consultGoal || '',
    consultPrice: Number(item.consultPrice ?? 0),
    consultDuration: item.consultDuration || '60 分鐘',
    bookingDate: item.bookingDate || '',
    bookingTime: item.bookingTime || '',
    bookingTimezone: item.bookingTimezone || '',
    date: item.payTime || item.gmtCreate || '',
    payTime: item.payTime || '',
    serialNumber: item.serialNumber ? String(item.serialNumber) : '',
    remark: item.remarkCus || '',
    status:
      item.orderStatus === 2
        ? 'paid' as const
        : item.orderStatus === 1
        ? 'pending' as const
        : 'closed' as const,
    bookingStatus: item.bookingStatus ?? 1,
    bookingStatusText:
      item.bookingStatus === 5
        ? '已完成'
        : item.bookingStatus === 4
        ? '已取消'
        : item.bookingStatus === 3
        ? '已確認'
        : item.bookingStatus === 2
        ? '待確認'
        : '待選時間'
  };
}

export function createCategoryNameResolver(categories: CategoryResp[]) {
  const map = categoryMapFromTree(categories);
  return (categoryId?: number | string) => map.get(idToKey(categoryId)) || '未分類';
}

export function createCategoryMetaResolver(categories: CategoryResp[]) {
  const map = new Map<string, { icon?: string; bg?: string }>();
  const walk = (items: CategoryResp[]) => {
    items.forEach((item) => {
      map.set(idToKey(item.id), { icon: item.categoryIcon, bg: item.categoryBg });
      if (item.list?.length) {
        walk(item.list);
      }
    });
  };
  walk(categories);
  return (categoryId?: number | string) => map.get(idToKey(categoryId)) || {};
}
